import { IPromise } from 'angular';
import { BehaviorSubject } from 'rxjs';
import { UserService } from '../../tn.user/service.tn.user';
import {
    FavoritesFilters,
    FavoritesRepository,
    IFavoriteList,
    INewFavoriteList,
    filterFavorite,
} from '../favorites-repository.service/favoritesRepository';

/*
 *  LocalFavorites stores a collection of the users favorites locally that controllers can bind to
 *  This allows the nav-bar and favorites page to stay in sync.
 */
export class LocalFavorites {
    favorites: IFavoriteList[] = [];
    favoriteFamilies: boolean[] = [];
    favoriteStyles: boolean[] = [];
    filteredFavorites: IFavoriteList[] = [];
    _updatePromise?: IPromise<void>;
    filters: FavoritesFilters = {};
    favorites$: BehaviorSubject<IFavoriteList[]> = new BehaviorSubject<
        IFavoriteList[]
    >([]);
    filteredFavorites$: BehaviorSubject<IFavoriteList[]> = new BehaviorSubject<
        IFavoriteList[]
    >([]);

    static $inject = ['favoritesRepository', 'tn.user.service'];
    constructor(
        protected favoriteRepository: FavoritesRepository,
        protected userService: UserService
    ) {
        if (userService.isLoggedIn()) {
            this._updateFavorites();
        }
        this.userService.login$.subscribe(() => {
            this._updateFavorites();
        });
        this.userService.logout$.subscribe(() => {
            this.favorites.splice(0, this.favorites.length);
            this.favoriteStyles.splice(0, this.favoriteStyles.length);
            this.favoriteFamilies.splice(0, this.favoriteFamilies.length);
            this.filteredFavorites.splice(0, this.filteredFavorites.length);
            this.favorites$.next([]);
            this.filteredFavorites$.next([]);
            this._updatePromise = undefined;
        });
    }

    _updateFavorites(): IPromise<void> {
        if (!this._updatePromise) {
            this._updatePromise = this.favoriteRepository
                .listFavorites()
                .catch((e) => {
                    this._updatePromise = undefined;
                    throw e;
                })
                .then((newFavs) => {
                    // clear old fav list
                    this.favorites.splice(0, this.favorites.length);
                    this.favoriteStyles.splice(0, this.favoriteStyles.length);
                    this.favoriteFamilies.splice(
                        0,
                        this.favoriteFamilies.length
                    );

                    // spread operator; causes all elements to spread out to single params
                    this.favorites.push(...newFavs);
                    this.setFilters(this.filters);

                    // mark styles as being favorited by id
                    this.favorites.forEach((f) =>
                        f.style.forEach((s) => {
                            this.favoriteStyles[s.id] = true;
                            this.favoriteFamilies[s.family] = true;
                        })
                    );
                    // we want to break the references with the subjects...
                    this.favorites$.next(newFavs);
                    this._updatePromise = undefined;
                });
        }

        return this._updatePromise;
    }

    // @deprecated use favorites$ instead.
    getFavorites(): IFavoriteList[] {
        return this.favorites;
    }

    getFavoriteFamilyIds(): boolean[] {
        return this.favoriteFamilies;
    }

    getFavoriteStyleIds(): boolean[] {
        return this.favoriteStyles;
    }

    isFavoriteStyle(id: number): boolean {
        return !!this.favoriteStyles[id];
    }

    setFilters(newFilters: FavoritesFilters): void {
        this.filters = newFilters;
        const filtered = this.favorites.filter((f) =>
            filterFavorite(f, this.filters)
        );
        this.filteredFavorites$.next(filtered);
        this.filteredFavorites.splice(0, this.filteredFavorites.length);
        this.filteredFavorites.push(...filtered);
    }

    // @deprecated use filteredFavorites$ instead.
    getFilteredFavorites(): IFavoriteList[] {
        return this.filteredFavorites;
    }

    getFavoriteList(id: number): IPromise<IFavoriteList> {
        return this.favoriteRepository.getFavoriteList(id);
    }

    emailFavoriteList(email: string, id: number): IPromise<unknown> {
        return this.favoriteRepository.emailFavoriteList(email, id);
    }

    deleteFavoriteList(id: number): IPromise<void> {
        return this.favoriteRepository
            .deleteFavoriteList(id)
            .then(() => this._updateFavorites());
    }

    saveFavoriteList(
        favList: IFavoriteList | INewFavoriteList
    ): IPromise<IFavoriteList> {
        const result = this.favoriteRepository.saveFavoriteList(favList);
        result.then(() => this._updateFavorites());
        return result;
    }
}
